<script setup lang="ts">
import { ref } from 'vue';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/20/solid'
import { AttributeConfig } from '@/interfaces/VCalendar';

const selectedDate = ref(new Date());

const selectAttribute = ref<AttributeConfig>({ dot: true });
const attributes: AttributeConfig[] = [
  // This is a single attribute
  {
    // An optional key can be used for retrieving this attribute later,
    // and will most likely be derived from your data object
    // Attribute type definitions
    // content: 'red',   // Boolean, String, Object
    // highlight: true,  // Boolean, String, Object
    dot: true,        // Boolean, String, Object
    // bar: true,        // Boolean, String, Object
    popover: {
      label: "Audience Creteil Transavia",
      isInteractive: true
    }, // Only objects allowed
    // Your custom data object for later access, if needed
    // We also need some dates to know where to display the attribute
    // We use a single date here, but it could also be an array of dates,
    //  a date range or a complex date pattern.
    dates: new Date(),
  },
  {
    // Boolean
    bar: true,
    dates: [new Date(2023, 4, 1), new Date(2023, 4, 9), new Date(2023, 4, 22)],
  },
  {
    // String
    bar: 'red',
    dates: [new Date(2018, 4, 4), new Date(2023, 4, 9), new Date(2023, 4, 15)],
  },
];
const date = ref(new Date())
function page({ to }) {
  date.value = to;
}
</script>
<template>
  <VCalendar show-weeknumbers="outside-right" expanded v-model="date" :attributes="attributes" @page="page" :rows="2"
    locale="fr" :select-attribute="selectAttribute" borderless transparent>
    <template #header-title="{ title }">
      <div class="flex-auto text-sm font-semibold capitalize">
        {{ title }}
      </div>
    </template>

    <template #header-prev-button="{ move, disabled }">
      <button type="button" :disabled="disabled" @click="move"
        class="-m-1.5 hover:bg-white border rounded-full flex flex-none items-center justify-center p-1.5 text-gray-400 hover:border-gray-400 hover:text-gray-500">
        <span class="sr-only">Mois précédent</span>
        <ChevronLeftIcon class="w-5 h-5" aria-hidden="true" />
      </button>
    </template>

    <template #header-next-button="{ move, disabled }">
      <button type="button" :disabled="disabled" @click="move"
        class="-m-1.5 border rounded-full flex flex-none items-center justify-center p-1.5 text-gray-400 hover:bg-white hover:border-gray-400 hover:text-gray-500">
        <span class="sr-only">Mois suivant</span>
        <ChevronRightIcon class="w-5 h-5" aria-hidden="true" />
      </button>
    </template>

    <template #day-content="{ day, attributes, locale }" class="bg-red-500 shadow">
      <button type="button" :class="[
        'isolate py-1.5 hover:bg-gray-100 focus:z-10 w-full border-t border-l',
        day.onBottom && 'border-b',
        day.onRight && 'border-r',
        day.inMonth ? 'bg-white' : 'bg-gray-50',
        (day.isSelected || day.isToday) && 'font-semibold',
        day.isSelected && 'text-white',
        !day.isSelected && day.inMonth && !day.isToday && 'text-gray-900',
        !day.inMonth && !day.isToday && 'text-gray-400',
        day.onTop && day.onLeft && 'rounded-tl-lg',
        day.onTop && day.onRight && 'rounded-tr-lg',
        day.onBottom && day.onLeft && 'rounded-bl-lg',
        day.onBottom && day.onRight && 'rounded-br-lg'
      ]">
        <time :datetime="day.date.toISOString().substring(0, 10)" :class="[
          'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
          day.isToday && 'text-indigo-100 rounded-full bg-indigo-700',
          day.isSelected && day.isToday && 'bg-indigo-600',
          day.isSelected && !day.isToday && 'bg-gray-900'
        ]">
          {{ day.day }}
        </time>
      </button>
    </template>

    <!-- <template #footer>
      <button type="button"
        class="w-full px-3 py-2 mt-8 text-sm font-semibold text-white rounded-md shadow bg-blue-upclaim hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-900">
        Ajouter audience
      </button>
    </template> -->

  </VCalendar>
</template>
<style>
.vc-weekday-1,
.vc-weekday-7 {
  @apply text-indigo-500
}

.vc-pane-header-wrapper {
  @apply text-center;
}

.vc-header { @apply capitalize; }
.vc-header .vc-arrow {
  @apply focus:ring-1 bg-inherit rounded-full border border-gray-200 hover:bg-gray-100 bg-white
}


/* .vc-week {
  @apply isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200
} */

.vc-weekday {
  @apply mt-6 grid font-normal text-xs leading-6 text-gray-500
}

.vc-week:has(.vc-weeknumber-content:empty) {
  display: none;
  /* Hide last row */
}

.vc-week:nth-last-child(2) {
  @apply shadow rounded-lg;
}

/**
 * Display not in month days
 * @see https://github.com/nathanreyes/v-calendar/issues/334#issuecomment-760193616
 */
.vc-container .vc-day-content.is-disabled {
  @apply pointer-events-none;
}

.vc-container .vc-day-content.is-disabled:hover {
  @apply bg-transparent;
}

.vc-day.is-not-in-month *:not(.is-disabled) {
  @apply opacity-100 pointer-events-auto;
}

.vc-day.is-not-in-month .is-disabled {
  @apply opacity-100 text-gray-400 pointer-events-none;
}</style>